import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FETCH_FORMS_LIST } from 'actions/formActions';
import { FaEye, FaSearch, FaTimes, FaChargingStation, FaCheckCircle, FaClock, FaFileAlt } from 'react-icons/fa';

const ContractorView = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading, allFormsList } = useSelector((state) => state.checklist);
    const [search, setSearch] = useState('');
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);

    useEffect(() => {
        dispatch({ type: FETCH_FORMS_LIST });
    }, [dispatch]);

    // Group forms by stationId
    const groupedByStation = useMemo(() => {
        return (allFormsList || []).reduce((acc, form) => {
            if (!form.stationId || typeof form.stationId !== 'string') return acc;
            if (!acc[form.stationId]) acc[form.stationId] = [];
            acc[form.stationId].push(form);
            return acc;
        }, {});
    }, [allFormsList]);

    // Build filtered and structured station data
    const filteredList = useMemo(() => {
        return Object.entries(groupedByStation)
            .filter(([, forms]) => forms.length > 0)
            .filter(([stationId]) => stationId.toLowerCase().includes(search.toLowerCase()))
            .map(([stationId, forms]) => {
                const totalForms = forms.length;
                const approvedForms = forms.filter((f) => (f.status || '').toLowerCase() === 'approved').length;
                const draftForms = forms.filter((f) => (f.status || '').toLowerCase() === 'draft').length;
                const pendingForms = totalForms - approvedForms - draftForms;

                const { project_name, userName } = forms[0];

                return {
                    stationId,
                    project_name: project_name || 'N/A',
                    userName: userName || 'N/A',
                    status: approvedForms === totalForms && totalForms > 0 ? 'completed' : 'in-progress',
                    formCount: totalForms,
                    approvedCount: approvedForms,
                    draftCount: draftForms,
                    pendingCount: pendingForms,
                };
            })
            .sort((a, b) => a.stationId.localeCompare(b.stationId));
    }, [groupedByStation, search]);

    // Summary stats
    const totalStations = filteredList.length;
    const totalApproved = filteredList.reduce((s, f) => s + f.approvedCount, 0);
    const totalPending = filteredList.reduce((s, f) => s + f.pendingCount, 0);
    const totalDraft = filteredList.reduce((s, f) => s + f.draftCount, 0);

    const statCards = [
        { label: 'Assigned Stations', value: totalStations, icon: <FaChargingStation size={20} />, gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
        { label: 'Approved', value: totalApproved, icon: <FaCheckCircle size={20} />, gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' },
        { label: 'Pending', value: totalPending, icon: <FaClock size={20} />, gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
        { label: 'Draft', value: totalDraft, icon: <FaFileAlt size={20} />, gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
    ];

    return (
        <div className="page-body checklist-container">
            <div className="container-xl">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2 className="page-title m-0" style={{ fontWeight: 800, letterSpacing: '-0.5px' }}>
                            My Assignments
                        </h2>
                        <p className="text-muted small mb-0 mt-1">Overview of stations and forms assigned to you</p>
                    </div>
                </div>

                {/* Stat Cards */}
                <div className="row g-3 mb-4">
                    {statCards.map((card) => (
                        <div className="col-6 col-lg-3" key={card.label}>
                            <div style={{
                                background: card.gradient,
                                borderRadius: '16px',
                                padding: '20px 22px',
                                color: '#fff',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                                transition: 'transform 0.2s ease',
                                cursor: 'default',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-3px)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                            >
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <span style={{ opacity: 0.85, fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{card.label}</span>
                                    <span style={{ opacity: 0.7 }}>{card.icon}</span>
                                </div>
                                <div style={{ fontSize: '28px', fontWeight: 800, lineHeight: 1 }}>{card.value}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Search */}
                <div className="premium-filter-section mb-4">
                    <div className={`filter-group search-wrapper ${search || isSearchExpanded ? 'expanded' : ''}`}>
                        <label htmlFor="station-search">Search Stations</label>
                        <div className="position-relative">
                            <Form.Control
                                id="station-search"
                                type="text"
                                placeholder="Search by Station ID..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onFocus={() => setIsSearchExpanded(true)}
                                onBlur={() => !search && setIsSearchExpanded(false)}
                            />
                            <FaSearch className="search-icon" />
                        </div>
                    </div>
                    {search && (
                        <div className="filter-group flex-grow-0">
                            <span className="filter-label-spacer" aria-hidden="true">&nbsp;</span>
                            <button id="clear-search-btn" className="btn-clear" onClick={() => setSearch('')} title="Clear Search">
                                <FaTimes /> Clear
                            </button>
                        </div>
                    )}
                </div>

                {/* Table */}
                {loading ? (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="text-muted mt-3">Loading stations...</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="premium-table">
                            <thead>
                                <tr>
                                    <th>Station</th>
                                    <th>Project</th>
                                    <th>Created By</th>
                                    <th style={{ textAlign: 'center' }}>Forms</th>
                                    <th style={{ textAlign: 'center' }}>Approved</th>
                                    <th style={{ textAlign: 'center' }}>Draft</th>
                                    <th style={{ textAlign: 'center' }}>Pending</th>
                                    <th style={{ textAlign: 'center' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredList.map((form) => (
                                    <tr key={form.stationId}>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <div style={{
                                                    width: '36px', height: '36px', borderRadius: '10px',
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}>
                                                    <FaChargingStation color="#fff" size={16} />
                                                </div>
                                                <div>
                                                    <span
                                                        className="fw-bold"
                                                        role="button"
                                                        tabIndex="0"
                                                        style={{ color: '#1e293b', fontSize: '14px', cursor: 'pointer' }}
                                                        onClick={() => navigate(`/formList/${form.stationId}`)}
                                                    >
                                                        {form.stationId}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td><span className="text-muted" style={{ fontSize: '13px' }}>{form.project_name}</span></td>
                                        <td><span className="text-muted" style={{ fontSize: '13px' }}>{form.userName}</span></td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span style={{
                                                background: '#f1f5f9', borderRadius: '8px', padding: '4px 12px',
                                                fontWeight: 700, fontSize: '13px', color: '#334155'
                                            }}>
                                                {form.formCount}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span style={{
                                                background: '#ecfdf5', borderRadius: '8px', padding: '4px 12px',
                                                fontWeight: 700, fontSize: '13px', color: '#059669'
                                            }}>
                                                {form.approvedCount}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span style={{
                                                background: '#eff6ff', borderRadius: '8px', padding: '4px 12px',
                                                fontWeight: 700, fontSize: '13px', color: '#2563eb'
                                            }}>
                                                {form.draftCount}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span style={{
                                                background: '#fffbeb', borderRadius: '8px', padding: '4px 12px',
                                                fontWeight: 700, fontSize: '13px', color: '#d97706'
                                            }}>
                                                {form.pendingCount}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <div className="action-btn-group" style={{ justifyContent: 'center' }}>
                                                <button
                                                    className="btn-premium"
                                                    title="View Station Forms"
                                                    onClick={() => navigate(`/formList/${form.stationId}`)}
                                                >
                                                    <FaEye />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filteredList.length === 0 && (
                                    <tr>
                                        <td colSpan="8" className="text-center py-5">
                                            <div className="d-flex flex-column align-items-center">
                                                <FaChargingStation size={48} className="text-muted mb-3" style={{ opacity: 0.3 }} />
                                                <p className="text-muted fw-bold mb-1">No assigned stations found</p>
                                                <p className="text-muted small">No forms have been assigned to you yet</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContractorView;
